import { useQuery } from '@tanstack/react-query';
import { supabase, tables } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { useErrorHandler } from '@/hooks/useErrorHandler';
import { ErrorCategory } from '@/utils/errorHandler';
import { useMemo } from 'react';

export interface VipFamily {
  id: string;
  family_name: string;
  students_count: number;
  attendance_percentage: number;
  payment_status: 'current' | 'ahead';
  months_ahead?: number;
  reason: string; // Why they're VIP
}

export interface VipFamiliesData {
  families: VipFamily[];
  count: number;
}

/**
 * Hook to fetch VIP families - those with excellent attendance and payment history
 */
export const useVipFamilies = () => {
  const { user } = useAuth();
  const errorHandler = useErrorHandler('useVipFamilies');
  
  const organizationId = useMemo(() => {
    return user?.user_metadata?.organization_id as string | undefined;
  }, [user?.user_metadata?.organization_id]);

  return useQuery({
    queryKey: ['vipFamilies', organizationId] as const,
    queryFn: async (): Promise<VipFamiliesData> => {
      if (!organizationId) {
        throw errorHandler.handleError(
          new Error('No organization ID found'),
          'fetchVipFamilies',
          { userId: user?.id },
          false
        );
      }

      try {
        // Get families with excellent billing status (current or ahead)
        const { data: goodFamilies, error: familiesError } = await tables
          .families()
          .select('id, name, billing_status')
          .eq('organization_id', organizationId)
          .in('billing_status', ['current']);

        if (familiesError) throw familiesError;

        if (!goodFamilies || goodFamilies.length === 0) {
          return { families: [], count: 0 };
        }

        // Get attendance data for the last 3 months for all families
        const threeMonthsAgo = new Date();
        threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
        const threeMonthsAgoStr = threeMonthsAgo.toISOString().split('T')[0];

        const familyIds = goodFamilies.map(family => family.id);

        // Get students for these families
        const { data: students, error: studentsError } = await tables
          .students()
          .select('id, family_id, first_name, last_name')
          .eq('organization_id', organizationId)
          .in('family_id', familyIds);

        if (studentsError) throw studentsError;

        if (!students || students.length === 0) {
          return { families: [], count: 0 };
        }

        // Get attendance records for these students in last 3 months
        const studentIds = students.map(student => student.id);
        const { data: attendanceRecords, error: attendanceError } = await tables
          .class_attendance()
          .select('student_id, status, date')
          .eq('organization_id', organizationId)
          .in('student_id', studentIds)
          .gte('date', threeMonthsAgoStr);

        if (attendanceError) throw attendanceError;

        // Calculate VIP families (>95% attendance)
        const vipFamilies: VipFamily[] = [];

        for (const family of goodFamilies) {
          const familyStudents = students.filter(s => s.family_id === family.id);
          
          if (familyStudents.length === 0) continue;

          let totalAttendance = 0;
          let totalClasses = 0;
          
          // Calculate attendance percentage for this family
          for (const student of familyStudents) {
            const studentAttendance = attendanceRecords?.filter(
              record => record.student_id === student.id
            ) || [];

            const classesAttended = studentAttendance.filter(
              record => record.status === 'present' || record.status === 'late'
            ).length;

            totalAttendance += classesAttended;
            totalClasses += studentAttendance.length;
          }

          if (totalClasses === 0) continue;

          const attendancePercentage = Math.round((totalAttendance / totalClasses) * 100);

          // VIP criteria: >95% attendance
          if (attendancePercentage >= 95) {
            let reason = '';
            if (attendancePercentage === 100) {
              reason = `Perfect attendance across ${familyStudents.length} student${familyStudents.length > 1 ? 's' : ''}`;
            } else {
              reason = `${attendancePercentage}% attendance & excellent payment history`;
            }

            vipFamilies.push({
              id: family.id,
              family_name: family.name || 'Unknown Family',
              students_count: familyStudents.length,
              attendance_percentage: attendancePercentage,
              payment_status: 'current', // We filtered for current families
              reason: reason,
            });
          }
        }

        // Sort by attendance percentage (highest first)
        vipFamilies.sort((a, b) => b.attendance_percentage - a.attendance_percentage);

        return {
          families: vipFamilies,
          count: vipFamilies.length,
        };
      } catch (error) {
        throw errorHandler.handleError(
          error,
          'fetchVipFamilies',
          { 
            userId: user?.id,
            organizationId,
            category: ErrorCategory.DATABASE
          },
          false
        );
      }
    },
    staleTime: 30 * 60 * 1000, // Consider data fresh for 30 minutes (less frequent updates)
    gcTime: 60 * 60 * 1000, // Keep unused data in cache for 1 hour
    retry: (failureCount, error) => {
      if (error?.message?.includes('organization_id')) return false;
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    refetchOnWindowFocus: false,
    enabled: !!organizationId,
  });
};